<!--标签页组件-->
<template>
  <div class="tabs-nav">
    <el-tabs
      v-model="activeTab"
      type="card"
      @tab-click="handleTabClick"
      @tab-remove="handleTabRemove"
      class="tabs-wrapper"
    >
      <!-- 首页标签 -->
      <el-tab-pane
        :key="'home-tab'"
        :closable="false"
        :name="dashboardPath"
      >
        <template #label>
          <div class="tab-label-content">
            <el-icon><HomeFilled /></el-icon>
            <span>首页</span>
          </div>
        </template>
      </el-tab-pane>

      <!-- 动态标签页 - 多重过滤确保不包含首页 -->
      <el-tab-pane
        v-for="tab in nonHomeTabs"
        :key="`tab-${tab.path}`"
        :name="tab.path"
        :label="tab.meta?.title || tab.name"
        closable
      >
        <template #label>
          <div class="tab-label-content">
            <el-icon v-if="tab.meta?.icon">
              <component :is="tab.meta.icon" />
            </el-icon>
            <span>{{ tab.meta?.title || tab.name }}</span>
          </div>
        </template>
      </el-tab-pane>
    </el-tabs>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, watch, onMounted, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useTabsStore, useMenuStore } from '@/stores'
import type { TabsPaneContext } from 'element-plus'
import { HomeFilled } from '@element-plus/icons-vue'

const route = useRoute()
const router = useRouter()
const tabsStore = useTabsStore()
const menuStore = useMenuStore()

const activeTab = ref('')
const dashboardPath = '/layout/dashboard'

// 判断是否为首页
const isHomeTab = (view: any) => {
  if (!view) return false
  if (view.path === dashboardPath) return true
  if (view.name === 'Dashboard') return true
  if (view.meta?.isHome === true) return true
  if (view.meta?.title === '课时概况') return true
  return false
}

// 严格过滤：绝对不包含首页，并且去重
const nonHomeTabs = computed(() => {
  // 确保是数组
  const views = Array.isArray(tabsStore.visitedViews) ? tabsStore.visitedViews : []
  // 过滤掉首页
  let filtered = views.filter(view => !isHomeTab(view))
  // 去重（基于路径）
  const unique = []
  const seenPaths = new Set()
  for (const view of filtered) {
    if (!seenPaths.has(view.path)) {
      seenPaths.add(view.path)
      unique.push(view)
    }
  }
  return unique
})

// 监听路由变化
watch(
  () => route.fullPath,
  async (newPath) => {
    activeTab.value = newPath
    if (!menuStore.initialized) {
      await nextTick()
      return
    }
    // 只有非首页才添加到 store
    const isHome = newPath === dashboardPath || route.meta?.isHome === true
    if (!isHome) {
      const exists = tabsStore.visitedViews.some(v => v.path === newPath)
      if (!exists) {
        tabsStore.addView(route)
      }
    }
  },
  { immediate: true }
)

// 监听 store 变化，自动清理首页
watch(
  () => tabsStore.visitedViews,
  (newViews) => {
    const hasHome = newViews.some(v => isHomeTab(v))
    if (hasHome) {
      console.warn('[Tabs] 自动清理 store 中的首页')
      const cleaned = newViews.filter(v => !isHomeTab(v))
      tabsStore.visitedViews = cleaned
      tabsStore.saveToLocalStorage()
    }
  },
  { deep: true, immediate: true }
)

// 初始化
onMounted(async () => {
  if (!menuStore.initialized) {
    await menuStore.initializeMenuData()
  }
  // 清理 store 中可能存在的首页
  const cleaned = tabsStore.visitedViews.filter(v => !isHomeTab(v))
  if (cleaned.length !== tabsStore.visitedViews.length) {
    tabsStore.visitedViews = cleaned
    tabsStore.saveToLocalStorage()
  }
  activeTab.value = route.fullPath
})

// 处理标签点击
const handleTabClick = (tab: TabsPaneContext) => {
  const path = tab.paneName as string
  if (path && path !== route.fullPath) {
    router.push(path)
  }
}

// 处理标签删除
const handleTabRemove = (name: string | number) => {
  const path = name as string
  if (path === dashboardPath) return
  if (path === activeTab.value) {
    const views = nonHomeTabs.value
    const index = views.findIndex(v => v.path === path)
    if (index > 0) {
      router.push(views[index - 1].path)
    } else if (index < views.length - 1 && views[index + 1]) {
      router.push(views[index + 1].path)
    } else {
      router.push(dashboardPath)
    }
  }
  tabsStore.delView(path)
}

const refreshCurrentTab = () => {
  const currentPath = activeTab.value
  router.replace('/redirect' + currentPath)
}

defineExpose({
  refreshCurrentTab,
  handleTabsAction: (command: string) => {
    const currentPath = activeTab.value
    switch (command) {
      case 'closeOthers':
        tabsStore.delOthersViews(currentPath)
        break
      case 'closeAll':
        tabsStore.delAllViews()
        router.push(dashboardPath)
        break
      case 'closeLeft':
        tabsStore.delLeftViews(currentPath)
        break
      case 'closeRight':
        tabsStore.delRightViews(currentPath)
        break
    }
  }
})
</script>
<style lang="scss" scoped>
.tabs-nav {
  background: #fff;
  border-bottom: 1px solid #ebebeb;
  height: 48px;
  line-height: 48px;
  width: 100%;
  --tab-icon-size: 18px;
  --home-icon-size: 18px;
  --close-icon-size: 12px;
  a {
    text-decoration: none;
    color: inherit;
    &:hover {
      text-decoration: none;
    }
  }
}

.el-tabs {
  width: 100% !important;
  height: 100% !important;
  padding: 0 20px !important;
}

/* 完全移除所有边框和底部线条 */
.tabs-nav :deep(.el-tabs) {
  border: none !important;
  box-shadow: none !important;
}

.tabs-nav :deep(.el-tabs__header) {
  margin: 0;
  height: 100%;
  display: flex;
  border: none !important;
  align-items: center;
  box-shadow: none !important;
}

.tabs-nav :deep(.el-tabs__nav-wrap) {
  padding-bottom: 0;
  overflow: hidden !important;
  border: none !important;
  box-shadow: none !important;
}

/* 移除底部的蓝色指示线 */
.tabs-nav :deep(.el-tabs__active-bar) {
  display: none !important;
}

/* 移除nav-wrap的底部边框 */
.tabs-nav :deep(.el-tabs__nav-wrap::after) {
  content: none !important;
}

.tabs-nav :deep(.el-tabs__nav-scroll) {
  overflow-x: auto !important;
  overflow-y: hidden !important;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
  border: none !important;
  box-shadow: none !important;
}

.tabs-nav :deep(.el-tabs__nav) {
  border: none !important;
  display: flex;
  align-items: center;
  flex-wrap: nowrap !important;
  white-space: nowrap !important;
  box-shadow: none !important;
}

.tabs-nav :deep(.el-tabs__item) {
  height: 36px;
  line-height: 36px;
  margin-right: 8px;
  border-radius: 5px;
  color: #6B778C !important;
  transition: all 0.3s ease;
  display: flex !important;
  border: none !important;
  align-items: center !important;
  justify-content: center !important;
  background: none !important;
  box-shadow: none !important;
  padding: 0 12px !important;
  min-width: 120px !important;
  width: auto !important;
  flex-shrink: 0 !important;
  white-space: nowrap !important;

  a {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    width: 100%;
    height: 100%;
    text-decoration: none !important;
    color: inherit !important;
  }

  &:hover {
    background-color: #f5f5f5 !important;
    border: none !important;
    .is-icon-close {
      opacity: 1 !important;
    }
  }

  &.is-active {
    background-color: #EAEEFD !important;
    color: #008dbc !important;
    border: none !important;
    .el-icon {
      color: #008dbc !important;
    }
    .is-icon-close {
      color: #008dbc !important;
      &:hover {
        color: #fff !important;
      }
    }
  }

  .el-icon {
    font-size: var(--tab-icon-size) !important;
    width: var(--tab-icon-size) !important;
    height: var(--tab-icon-size) !important;
    color: #6B778C !important;
    align-items: center !important;
    justify-content: center !important;
    flex-shrink: 0 !important;
    margin-right: 6px !important;
    margin-top: -1px !important;
    vertical-align: middle !important;

    svg {
      vertical-align: -0.15em !important;
    }
  }

  &::before,
  &::after {
    content: none !important;
  }
}

.tabs-nav :deep(.el-tabs__item .tab-label-content) {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  width: 100% !important;
  height: 100% !important;

  .el-icon {
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    margin-right: 6px !important;
  }

  span {
    display: inline-flex !important;
    align-items: center !important;
    height: 100% !important;
    font-size: 14px !important;
    margin-right: 10px !important;
    line-height: 1 !important;
    vertical-align: middle !important;
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    max-width: 150px !important;
  }
}

.tabs-nav :deep(.el-tabs__item span) {
  display: flex !important;
  align-items: center !important;
  flex-shrink: 1 !important;

  span {
    line-height: 1 !important;
    vertical-align: middle !important;
    display: inline-block !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    white-space: nowrap !important;
    max-width: 150px !important;
    min-width: 20px !important;
  }
}

.tabs-nav :deep(.el-tabs__item) {
  &::after {
    display: none !important;
  }
}

.tabs-nav :deep(.el-tabs__item .el-tabs__item-content) {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  width: 100% !important;
  flex-wrap: nowrap !important;
}

.tabs-nav :deep(.el-tabs__item-label) {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  width: 100% !important;
  overflow: hidden !important;
}

.tabs-nav :deep(.is-icon-close) {
  font-size: var(--close-icon-size) !important;
  width: var(--close-icon-size) !important;
  height: var(--close-icon-size) !important;
  border-radius: 50% !important;
  color: #fff !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  transition: all 0.3s ease !important;
  transform: scale(0.8);

  &:hover {
    background-color: rgba(224, 11, 39, 0.9) !important;
    color: #fff !important;
  }
}

.tabs-nav :deep(.el-tabs__nav-scroll) {
  width: 100% !important;
  min-width: 100% !important;
}

.tabs-nav :deep(.el-tabs__nav) > * {
  flex: 0 0 auto !important;
}

.tabs-nav :deep(.el-tabs__item.long-text) {
  padding: 0 16px !important;
}

.tabs-nav :deep(*) {
  box-shadow: none !important;
  outline: none !important;
}

.tabs-nav :deep(.el-tabs__item:focus) {
  outline: none !important;
  border: none !important;
}

/* PC端响应式 */
@media screen and (max-width: 1200px) {
  .tabs-nav :deep(.el-tabs__item span span) {
    max-width: 100px !important;
  }
}

/* ========== 手机端适配 - 缩小标签页尺寸 ========== */
@media screen and (max-width: 768px) {
  .tabs-nav {
    height: 40px;
    line-height: 40px;
    --tab-icon-size: 14px;
    --close-icon-size: 10px;
  }

  .el-tabs {
    padding: 0 12px !important;
  }

  .tabs-nav :deep(.el-tabs__item) {
    height: 30px;
    line-height: 30px;
    margin-right: 6px;
    border-radius: 4px;
    padding: 0 8px !important;
    min-width: 70px !important;

    .el-icon {
      font-size: 14px !important;
      width: 14px !important;
      height: 14px !important;
      margin-right: 4px !important;
    }

    .tab-label-content span {
      font-size: 12px !important;
      margin-right: 6px !important;
      max-width: 80px !important;
    }

    span span {
      font-size: 12px !important;
      max-width: 70px !important;
    }
  }

  .tabs-nav :deep(.is-icon-close) {
    font-size: 14px !important;
    width: 14px !important;
    height: 14px !important;
    transform: scale(0.8);
  }

  .tabs-nav :deep(.el-tabs__item span span) {
    max-width: 60px !important;
  }
}

/* 小屏手机 */
@media screen and (max-width: 480px) {
  .tabs-nav {
    height: 36px;
    line-height: 36px;
    --tab-icon-size: 12px;
    --close-icon-size: 9px;
  }

  .el-tabs {
    padding: 0 8px !important;
  }

  .tabs-nav :deep(.el-tabs__item) {
    height: 28px;
    line-height: 28px;
    margin-right: 4px;
    padding: 0 6px !important;
    min-width: 55px !important;
    .el-icon {
      font-size: 14px !important;
      width: 14px !important;
      height: 14px !important;
      margin-right: 3px !important;
    }
    .tab-label-content span {
      font-size: 11px !important;
      margin-right: 4px !important;
      max-width: 60px !important;
    }
    span span {
      font-size: 11px !important;
      max-width: 50px !important;
    }
  }

  .tabs-nav :deep(.el-tabs__item span span) {
    max-width: 50px !important;
  }
}
</style>
